import { cn } from '@rocco/utils/cn';
import {
    BLEED_BREAKOUT_X,
    BLEED_GUTTER_X,
    NumberedAccordion,
    type NumberedAccordionRow,
} from '~/ui-rocco/pages/detail-shared';
import { useProfileFamily } from '@modules/professional/hook/use-profile-family';
import { useMatchesHandleData } from '~/modules/root/hook';
import { ProfessionalBrandFamilyCarousel } from './ProfessionalBrandFamilyCarousel';

/** The preview's rows are held open, so a toggle can never reach this. */
const NOOP_VALUE_CHANGE = () => undefined;

/** The Figma draws this row expanded, so the family is visible on arrival. */
const BRAND_FAMILY_ROW = 'Explore our Brand Family';

/**
 * Overview accordion (Figma 2354:58614, 5498:66827) — "Explore our Brand
 * Family". The row self-hides when the profile isn't in a network, and the
 * whole accordion drops out when no row applies.
 *
 * Book a Meeting used to lead this accordion and no longer does: the header
 * carries that CTA on every tab and the Team section offers each member
 * directly, so a row here was the same action asked for a third time (BC-5174).
 * Testimonials used to sit here and now close the About tab's accordion.
 */
export const ProfessionalOverviewAccordionSection = () => {
    const family = useProfileFamily();
    const isBMPreview = useMatchesHandleData('isBMPreview', false);

    const rows: NumberedAccordionRow[] = [];

    if (family) {
        rows.push({
            title: BRAND_FAMILY_ROW,
            // The Figma runs the cards straight off the trigger row — the row's
            // own padding is the whole gap.
            content: <ProfessionalBrandFamilyCarousel />,
            // Widen the clip Radix puts on the animating content box out to the
            // viewport edges (negative margin + equal padding leaves the content
            // where it is), so the carousel's slides bleed through the page
            // gutter instead of vanishing at it.
            contentClassName: cn(
                'max-md:-mx-5 max-md:px-5',
                BLEED_BREAKOUT_X,
                BLEED_GUTTER_X,
            ),
        });
    }

    // Business Manager's preview is not clickable, so a collapsed row is a row
    // the author cannot open. Without a brand family nothing here opens at all,
    // which left authored testimonials completely invisible while being edited.
    // Hold every row open there; visitors keep the real default.
    const openRows = isBMPreview ? rows.map((row) => row.title) : undefined;

    return (
        <NumberedAccordion
            headingLevel={2}
            theme="light"
            multiple
            rows={rows}
            value={openRows}
            onValueChange={NOOP_VALUE_CHANGE}
            defaultOpenTitles={family ? [BRAND_FAMILY_ROW] : undefined}
            // With the open card grid last, its own bottom edge closes the
            // block — a closing hairline would float as a stray separator
            // above the About band, and the Figma omits it.
            hideLastBorder={!!family}
            triggerClassName="md:[&>svg]:size-8"
        />
    );
};
